import React, { useState, useEffect } from "react";
import { useAuth } from "../../AuthContext";
import "./PaymentWidget.css";
import { ReactComponent as Refresh } from "../../icons/refresh.svg";
import { Table } from "../../lv1ui";

const topUpPresets = [10, 50, 100, 250, 500];

const formatAmount = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return "0.00";

    return number.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6
    });
};

const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";

    return date.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const getPaymentMethod = (type) => {
    if (type === "crypto") return "USDT (TRC20)";
    if (type === "manual") return "Manager";
    if (type === "manual_withdraw") return "Manager";
    return type || "-";
};

const getPaymentStatus = (payment) => {
    if (Number(payment.is_paid) === 1) return "Completed";
    return "Pending";
};

const getPaymentTypeLabel = (payment) => {
    const debtAmount = Number(payment.debt_amount) || 0;

    if (payment.type === "manual" && debtAmount > 0) return "Credit top up";
    if (payment.type === "manual") return "Manual top up";
    if (payment.type === "manual_withdraw" && debtAmount > 0) return "Debt write-off";
    if (payment.type === "manual_withdraw") return "Manual withdraw";
    return "Crypto top up";
};

const getCreditChangeValue = (payment) => {
    const debtAmount = Number(payment.debt_amount) || 0;
    if (debtAmount <= 0) return "No credit change";

    return `${payment.type === "manual_withdraw" ? "-" : "+"}${formatAmount(debtAmount)} USDT`;
};

export function PaymentWidget() {
    const [amount, setAmount] = useState("");
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [iPaid, setIPaid] = useState(false);
    const [reloading, setReloading] = useState(false);
    const [payments, setPayments] = useState([]);
    const [paymentsLoading, setPaymentsLoading] = useState(false);
    const [paymentError, setPaymentError] = useState("");

    const { user, fetchWithAuth, reloadUser } = useAuth();

    const loadPaymentHistory = async () => {
        setPaymentsLoading(true);

        try {
            const res = await fetchWithAuth("/api/payment-history");
            if (!res) return;

            const data = await res.json();

            if (res.ok && data.success) {
                setPayments(data.payments || []);
            }
        } catch (error) {
            console.error("Error loading payment history:", error);
        } finally {
            setPaymentsLoading(false);
        }
    };

    const handleCreatePayment = async () => {
        const parsedAmount = Number(amount);

        if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
            setPaymentError("Enter a valid amount");
            return;
        }

        setPaymentInfo(false);
        setIPaid(false);
        setPaymentError("");

        try {
            const res = await fetchWithAuth("/api/create-payment", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ amount: parsedAmount })
            });

            if (!res) {
                setPaymentError("Payment creation failed");
                return;
            }

            const data = await res.json();

            if (!res.ok || !data.success) {
                setPaymentError(data.error || "Payment creation failed");
                return;
            }

            setPaymentInfo(data);
            await loadPaymentHistory();
        } catch (error) {
            console.error("Error creating payment:", error);
            setPaymentError("Payment creation failed");
        }
    };

    const handleIPaid = () => {
        setIPaid(true);

        [60000, 180000, 300000, 600000].forEach((delay) => {
            setTimeout(() => {
                reloadUser();
                loadPaymentHistory();
            }, delay);
        });
    };

    const handleReload = async () => {
        if (reloading) return;

        setReloading(true);
        await Promise.all([
            reloadUser(),
            loadPaymentHistory(),
            new Promise((res) => setTimeout(res, 500))
        ]);
        setReloading(false);
    };

    useEffect(() => {
        const listener = () => handleReload();
        window.addEventListener("force-balance-reload", listener);

        return () => window.removeEventListener("force-balance-reload", listener);
    }, []);

    useEffect(() => {
        loadPaymentHistory();
    }, []);

    const paymentColumns = [
        { key: "created_at", label: "Date" },
        { key: "type", label: "Type" },
        { key: "amount", label: "Amount" },
        { key: "credit", label: "Credit/Debt" },
        { key: "method", label: "Method" },
        { key: "status", label: "Status" }
    ];

    const availableBalance = Number(user?.balance) || 0;
    const creditLimit = Number(user?.debt) || 0;
    const ownFunds = availableBalance - creditLimit;
    const creditUsed = ownFunds < 0 ? Math.abs(ownFunds) : 0;

    const paymentRows = payments.map((payment) => ({
        ...payment,
        created_at: formatDate(payment.paid_at || payment.created_at),
        type: getPaymentTypeLabel(payment),
        amount: (
            <span className={payment.type === "manual_withdraw" ? "paymentWidgetAmountNegative" : "paymentWidgetAmountPositive"}>
                {payment.type === "manual_withdraw" ? "-" : "+"}{formatAmount(payment.amount)} USDT
            </span>
        ),
        credit: (
            <span className={payment.type === "manual_withdraw" && Number(payment.debt_amount) > 0 ? "paymentWidgetAmountNegative" : ""}>
                {getCreditChangeValue(payment)}
            </span>
        ),
        method: getPaymentMethod(payment.type),
        status: getPaymentStatus(payment)
    }));

    return (
        <div className="paymentWidgetContainer">
            <section className="paymentWidgetTopUpCard">
                <div className="paymentWidgetTopBar">
                    <div>
                        <div className="paymentWidgetLabel">Available balance</div>
                        <div className="paymentWidgetBalanceLine">
                            {!reloading ? (
                                <>
                                    <span className="paymentWidgetBalanceValue">{formatAmount(availableBalance)}</span>
                                    <span className="paymentWidgetBalanceCurrency">USDT</span>
                                </>
                            ) : (
                                <span className="shimmer paymentWidgetBalanceShimmer" />
                            )}
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleReload}
                        className={
                            reloading
                                ? "paymentWidgetReloadBalanceSpin paymentWidgetIconButton"
                                : "paymentWidgetReloadBalance paymentWidgetIconButton"
                        }
                        aria-label="Refresh balance"
                    >
                        <Refresh />
                    </button>
                </div>

                <div className="paymentWidgetBalanceBreakdown">
                    <div className="paymentWidgetBreakdownItem">
                        <span>Credit limit</span>
                        <strong>{formatAmount(creditLimit)} USDT</strong>
                    </div>
                    <div className="paymentWidgetBreakdownItem">
                        <span>{ownFunds >= 0 ? "Own funds" : "Own funds balance"}</span>
                        <strong className={ownFunds < 0 ? "negative" : ""}>
                            {ownFunds < 0 ? "-" : ""}{formatAmount(Math.abs(ownFunds))} USDT
                        </strong>
                    </div>
                    {creditUsed > 0 && (
                        <div className="paymentWidgetBreakdownItem warning">
                            <span>Credit used</span>
                            <strong>{formatAmount(creditUsed)} USDT</strong>
                        </div>
                    )}
                </div>

                <div className="paymentWidgetDivider" />

                <div className="paymentWidgetFormGrid">
                    <label className="paymentWidgetAmountField">
                        <span className="paymentWidgetLabel">Top up balance</span>
                        <div className="paymentWidgetInputWrap">
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                placeholder="Enter amount"
                                className="paymentWidgetAddNumber"
                            />
                            <span>USDT</span>
                        </div>
                    </label>

                    <div className="paymentWidgetPresets" aria-label="Top up presets">
                        {topUpPresets.map((value) => (
                            <button
                                key={value}
                                type="button"
                                className={Number(amount) === value ? "paymentWidgetPreset active" : "paymentWidgetPreset"}
                                onClick={() => setAmount(String(value))}
                            >
                                {value}
                            </button>
                        ))}
                    </div>

                    <button type="button" onClick={handleCreatePayment} className="addBalanceButton">
                        <div className="addBalanceButtonText">Top up</div>
                    </button>
                </div>

                {paymentError && <div className="paymentWidgetError">{paymentError}</div>}

                {paymentInfo && (
                    <div className="paymentWidgetPaymentCard">
                        {iPaid ? (
                            <div className="paymentWidgetPaymentInfo">
                                <div>Thank you for your payment.</div>
                                <div>We will automatically verify the receipt of funds and update your balance.</div>
                            </div>
                        ) : (
                            <>
                                <div className="paymentWidgetPaymentInfo">
                                    <div>Payment ID: <span>{paymentInfo?.paymentId}</span></div>
                                    <div>Send <span>{paymentInfo?.expectedAmount}</span> USDT (TRC20)</div>
                                    <div className="paymentWidgetAddress">{paymentInfo?.address}</div>
                                    <div>Send the exact amount within 1 hour.</div>
                                </div>

                                <button type="button" onClick={handleIPaid} className="paymentWidgetIPaidButton">
                                    <div className="paymentWidgetIPaidButtonText">I have paid</div>
                                </button>
                            </>
                        )}
                    </div>
                )}
            </section>

            <section className="paymentWidgetHistoryCard">
                <div className="paymentWidgetHistoryHeader">
                    <div>Transaction history</div>
                    {paymentsLoading && <span>Loading...</span>}
                </div>

                {paymentRows.length > 0 ? (
                    <Table height="340px" columns={paymentColumns} rows={paymentRows} />
                ) : (
                    <div className="paymentWidgetEmptyHistory">
                        {paymentsLoading ? "Loading payment history..." : "No transactions yet"}
                    </div>
                )}
            </section>
        </div>
    );
}
